import Box from "@material-ui/core/Box"
import Button from "@material-ui/core/Button"
import Card from "@material-ui/core/Card"
import CardContent from "@material-ui/core/CardContent"
import CardHeader from "@material-ui/core/CardHeader"
import CircularProgress from "@material-ui/core/CircularProgress"
import Grid from "@material-ui/core/Grid"
import RadioGroup from "@material-ui/core/RadioGroup"
import Typography from "@material-ui/core/Typography"
import Alert from "@material-ui/lab/Alert"
import { useSnackbar } from "notistack"
import React from "react"
import { useAppContext } from "../../../app-context/use-app-context"
import { HourReportWorkflow, useGetHourReportSettingsQuery, useUpdateHourReportSettingsMutation } from "../../api"
import { HourReportSettings } from "../../api/hour-report-settings"
import ApprovalWorkflow from "./ApprovalWorkflow"
import CheckAndApprovalWorkflow from "./CheckAndApprovalWorkflow"
import ConfirmHourReportWorkflowDialog from "./ConfirmHourReportWorkflowDialog"
import HourReportReviewProcess from "./HourReportReviewProcess"
import ReportingOnlyWorkflow from "./ReportingOnlyWorkflow"
import { useHourReportWorkflowTranslations } from "./use-translations"

type HourReportWorkflowSettingsProps = {
  onWorkflowPreviewChange: (workflow: HourReportWorkflow | null) => void
}

type WorkflowDraft = {
  workflow: HourReportWorkflow
  reviewRequiresSubmission: boolean
  approvalRequiresChecking: boolean
}

const getWorkflowFormState = (draft: WorkflowDraft | null, savedSettings?: HourReportSettings) => {
  const savedWorkflow = savedSettings?.workflow
  const workflow = draft?.workflow ?? savedWorkflow
  const reviewRequiresSubmission = draft?.reviewRequiresSubmission ?? savedSettings?.reviewRequiresSubmission ?? false
  const approvalRequiresChecking = draft?.approvalRequiresChecking ?? savedSettings?.approvalRequiresChecking ?? false

  return {
    workflow,
    reviewRequiresSubmission,
    approvalRequiresChecking,
    requiresApproval: workflow === HourReportWorkflow.Approval || workflow === HourReportWorkflow.CheckAndApproval,
    hasChanges:
      draft !== null &&
      (draft.workflow !== savedWorkflow ||
        draft.reviewRequiresSubmission !== savedSettings?.reviewRequiresSubmission ||
        draft.approvalRequiresChecking !== savedSettings?.approvalRequiresChecking),
  }
}

const HourReportWorkflowSettings = ({ onWorkflowPreviewChange }: HourReportWorkflowSettingsProps) => {
  const orgId = useAppContext().appContext.mainContext?.id as string
  const translations = useHourReportWorkflowTranslations()
  const { enqueueSnackbar } = useSnackbar()
  const { data, isLoading, isError } = useGetHourReportSettingsQuery(orgId)
  const [updateSettings, { isLoading: isSaving, isError: isSaveError }] = useUpdateHourReportSettingsMutation()
  const [draft, setDraft] = React.useState<WorkflowDraft | null>(null)
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = React.useState(false)

  const { workflow, reviewRequiresSubmission, approvalRequiresChecking, requiresApproval, hasChanges } =
    getWorkflowFormState(draft, data?.hourReportSettings)

  const updateDraftWorkflow = (nextWorkflow: HourReportWorkflow) => {
    if (nextWorkflow === workflow) return

    setDraft({
      workflow: nextWorkflow,
      reviewRequiresSubmission: nextWorkflow !== HourReportWorkflow.ReportingOnly,
      approvalRequiresChecking: nextWorkflow === HourReportWorkflow.CheckAndApproval,
    })
    onWorkflowPreviewChange(nextWorkflow)
  }

  const updateRequirement = (requirement: keyof Omit<WorkflowDraft, "workflow">, checked: boolean) => {
    if (!workflow) return
    setDraft({ workflow, reviewRequiresSubmission, approvalRequiresChecking, [requirement]: checked })
  }

  const cancelWorkflowChanges = () => {
    setDraft(null)
    onWorkflowPreviewChange(null)
  }

  const saveWorkflow = () => {
    if (!draft || !hasChanges) return

    updateSettings({ orgId, ...draft })
      .unwrap()
      .then(() => {
        setIsConfirmDialogOpen(false)
        cancelWorkflowChanges()
        enqueueSnackbar(translations.saveSuccessMessage, { variant: "success" })
      })
      .catch(() => setIsConfirmDialogOpen(false))
  }

  return (
    <Box
      mt={3}
      maxWidth={requiresApproval ? "100%" : 600}
      mx="auto"
      style={{
        transition: "max-width 0.3s ease-in-out, height 0.3s ease-in-out",
      }}
    >
      <Card variant="outlined">
        <CardHeader
          title={translations.title}
          subheader={translations.description}
          titleTypographyProps={{ variant: "h5", gutterBottom: true }}
          subheaderTypographyProps={{ variant: "body2" }}
        />
        <CardContent style={{ padding: 24, margin: 0, paddingTop: 0 }}>
          {isLoading && <CircularProgress size={24} />}
          {(isError || (!isLoading && !data)) && <Alert severity="error">{translations.loadError}</Alert>}
          {isSaveError && <Alert severity="error">{translations.saveError}</Alert>}
          {data && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={requiresApproval ? 6 : 12}>
                <RadioGroup
                  aria-label={translations.title}
                  value={workflow}
                  onChange={(event) => updateDraftWorkflow(event.target.value as HourReportWorkflow)}
                >
                  <ReportingOnlyWorkflow disabled={!data.canUpdateWorkflow || isSaving} />
                  <ApprovalWorkflow
                    selected={workflow === HourReportWorkflow.Approval}
                    disabled={!data.canUpdateWorkflow || isSaving}
                    reviewRequiresSubmission={reviewRequiresSubmission}
                    onReviewRequiresSubmissionChange={(checked) =>
                      updateRequirement("reviewRequiresSubmission", checked)
                    }
                  />
                  <CheckAndApprovalWorkflow
                    selected={workflow === HourReportWorkflow.CheckAndApproval}
                    disabled={!data.canUpdateWorkflow || isSaving}
                    reviewRequiresSubmission={reviewRequiresSubmission}
                    approvalRequiresChecking={approvalRequiresChecking}
                    onReviewRequiresSubmissionChange={(checked) =>
                      updateRequirement("reviewRequiresSubmission", checked)
                    }
                    onApprovalRequiresCheckingChange={(checked) =>
                      updateRequirement("approvalRequiresChecking", checked)
                    }
                  />
                </RadioGroup>
                {hasChanges && (
                  <Box mt={3} display="flex">
                    <Button
                      color="primary"
                      variant={hasChanges ? "contained" : "outlined"}
                      disabled={isSaving || !hasChanges}
                      onClick={() => setIsConfirmDialogOpen(true)}
                    >
                      {translations.saveButtonLabel}
                    </Button>
                    <Box ml={1}>
                      <Button disabled={isSaving} onClick={cancelWorkflowChanges}>
                        {translations.cancelButtonLabel}
                      </Button>
                    </Box>
                  </Box>
                )}
              </Grid>
              {workflow && requiresApproval && (
                <Grid item xs={12} md={6}>
                  <Box p={2} height="100%" bgcolor="action.hover" borderRadius={4} style={{ boxSizing: "border-box" }}>
                    <Typography variant="subtitle2" align="center" gutterBottom>
                      {translations.reviewProcessLabel}
                    </Typography>
                    <HourReportReviewProcess
                      workflow={workflow}
                      reviewRequiresSubmission={reviewRequiresSubmission}
                      approvalRequiresChecking={approvalRequiresChecking}
                    />
                  </Box>
                </Grid>
              )}
            </Grid>
          )}
        </CardContent>
      </Card>
      {draft && (
        <ConfirmHourReportWorkflowDialog
          open={isConfirmDialogOpen}
          isSaving={isSaving}
          workflow={draft.workflow}
          reviewRequiresSubmission={draft.reviewRequiresSubmission}
          approvalRequiresChecking={draft.approvalRequiresChecking}
          onConfirm={saveWorkflow}
          onClose={() => setIsConfirmDialogOpen(false)}
        />
      )}
    </Box>
  )
}

export default HourReportWorkflowSettings
